import React, { FC, useEffect, useState } from 'react';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { fetchWorkflow, updateWorkflow } from '../../../store/merchnts/actions/workflows';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../../components/Spinner';
import { SectionWrapper } from '../../../components/SectionWrapper';
import { useFormik } from 'formik';
import { InitialValuesTypes } from '../../../store/merchnts/merchant-interfaces';
import { Title } from '../../../components/developers/page-components/Title';
import { GeneralSwitches } from '../../../components/merchants/GeneralSwitches';
import { MerchantConfigurationInputBlock } from '../../../components/merchants/MerchantConfigurationInputBlock';

const MerchantWorkflowsPage: FC = () => {
    const { id: merchantId } = useParams<{ id: string }>();
    const { merchantState: { isWorkflowsLoading, workflows, workflowsData }, dispatch } = useMerchants();

    const [isEditMode, setIsEditMode] = useState<{[key: string]: boolean}>({});
    const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

    const formik = useFormik<InitialValuesTypes | any>({
        initialValues: {},
        onSubmit: () => void 0
    });

    useEffect(() => {
        fetchWorkflow(dispatch, merchantId);
    }, []);

    const onEditClick = (id: string): void => {
        setIsButtonLoading(true);

        const values = {};

        workflowsData?.forEach((workflow) => {
            const value = formik.values[workflow.id];
            const isTouched = formik.touched[workflow.id];

            if (workflow.type === 'IntegerValue') {
                if (isTouched) {
                    values[workflow.id] = value === null || value === undefined || value === '' ? null : Number(value);
                }
            } else {
                if (isTouched) {
                    values[workflow.id] = formik.values[workflow.id];
                } else if (workflow.value !== undefined) {
                    values[workflow.id] = workflow.value;
                }
            }
        });

        updateWorkflow(merchantId, { ...values })
            .finally(() => {
                setIsEditMode({ ...isEditMode, [id]: false });
                setIsButtonLoading(false);
                fetchWorkflow(dispatch, merchantId);
            });

    };

    const onCancelClick = (id: string): void => {
        setIsEditMode({ ...isEditMode, [id]: false });
    };

    useEffect(() => {
        const formikNewValues = {};

        workflowsData?.forEach((workflowDataItem) => {

            if (workflowDataItem.type === 'IntegerValue') {
                formikNewValues[workflowDataItem.id] = workflowDataItem.value;
            } else {
                formikNewValues[workflowDataItem.id] = workflowDataItem.value ?? workflowDataItem.defaultValue === 'true';
            }
        });

        formik.setValues(formikNewValues);
    }, [workflowsData]);

    if (isWorkflowsLoading) {
        return (

            <div className="card h-100">
                <div className="card-body " style={{ minHeight: '200px' }}>
                    <Spinner pageLoader/>
                </div>
            </div>
        );
    }

    const showPlaceholder = !workflows || typeof workflows === 'object' && Object.keys(workflows).length === 0 || typeof workflows !== 'object' ||
        !workflowsData || workflowsData?.length === 0;

    return (
        <>
            {
                Object.keys(workflows || {})?.map((category, index) => (
                    <SectionWrapper
                        showPlaceholder={showPlaceholder}
                        key={ `${category}_index`}
                        title={category}
                        sectionAdditionalClass="mb-5 min-height-200"
                        isEditMode={isEditMode[`${category}_index`]}
                        setIsEditMode={(): void => setIsEditMode({ ...isEditMode, [`${category}_index`]: !isEditMode[`${category}_index`] })}
                        onEditClick={(): void => onEditClick(`${category}_index`)}
                        onCancelClick={(): void => onCancelClick(`${category}_index`)}
                        loading={isButtonLoading}
                        pageLoading={isWorkflowsLoading}
                        isCardBody
                        isEditBlock={!showPlaceholder}
                    >
                        <div className="d-sm-flex flex-wrap category-container">
                            {Object.keys(workflows[category] || {})?.map(( subCategory, index ) => (
                                <div key={`${subCategory}_${index}`} className="category-card mt-5 ms-5 p-5">
                                    <Title title={subCategory} additionalClass="fs-6 fw-bolder mb-5"/>

                                    {
                                        workflows[category][subCategory]
                                            .filter(el => el.type === 'OffSwitch')
                                            .map((item) => (
                                            <GeneralSwitches
                                                key={item.id}
                                                formik={formik}
                                                formEditStatus={isEditMode[`${category}_index`] && item.enabled}
                                                title={item.name}
                                                formField={item.id}
                                                tooltip={item.description}
                                            />
                                        ))
                                    }

                                    {
                                        workflows[category][subCategory]
                                            .filter(el => el.type === 'IntegerValue')
                                            .map((item) => {
                                                return (
                                                    <MerchantConfigurationInputBlock
                                                        key={item.id}
                                                        formik={formik}
                                                        formEditStatus={isEditMode[`${category}_index`] && item.enabled}
                                                        title={item.name}
                                                        formField={item.id}
                                                        tooltip={item.description}
                                                        max={item.maxValue}
                                                        min={item.minValue}
                                                        placeholder={formik.values[item.id] ?? item.defaultValue}
                                                    />
                                                );
                                            })
                                    }
                                </div>
                            ))}
                        </div>
                    </SectionWrapper>
                ))
            }
        </>
    );
};

export default MerchantWorkflowsPage;
